import { useState, useEffect } from 'react';
import { getAvatarUrl } from '../../utils/avatar';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, User, Calendar, BookOpen, CheckCircle, XCircle, Clock, Edit, Save } from 'lucide-react';
import api from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal';

export default function LecturerStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [marks, setMarks] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(false);
  const [attendanceDraft, setAttendanceDraft] = useState({});
  const [showAttendanceConfirmation, setShowAttendanceConfirmation] = useState(false);
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [attendanceDateFilter, setAttendanceDateFilter] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
  const [showAttendanceDateFilter, setShowAttendanceDateFilter] = useState(false);

  const formatDateTime = (value) => {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never';
    return date.toLocaleString();
  };

  useEffect(() => { loadData(); }, [studentId]);

  const loadData = async () => {
    try {
      const res = await api.get(`/lecturer/students/${studentId}`);
      if (res.data.success) {
        setStudent(res.data.data.student);
        setAttendance(res.data.data.attendance || []);
        setMarks(res.data.data.marks || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const startAttendanceEdit = () => {
    setAttendanceDraft(Object.fromEntries(attendance.map(record => [
      String(record.session_id),
      record.status === 'Present' ? 'Present' : 'Absent'
    ])));
    setEditingAttendance(true);
  };

  const cancelAttendanceEdit = () => {
    setEditingAttendance(false);
    setAttendanceDraft({});
  };

  const toggleAttendanceStatus = (record) => {
    setAttendanceDraft(prev => ({
      ...prev,
      [String(record.session_id)]: (prev[String(record.session_id)] || record.status) === 'Present'
        ? 'Absent'
        : 'Present'
    }));
  };

  const saveAttendanceChanges = async () => {
    const records = attendance
      .filter(record => attendanceDraft[String(record.session_id)])
      .map(record => ({
        sessionId: record.session_id,
        status: attendanceDraft[String(record.session_id)]
      }));

    if (!records.length) return;

    try {
      setSavingAttendance(true);
      setError('');
      const res = await api.post('/lecturer/attendance/student-records', {
        studentId,
        records
      });
      if (res.data.success) {
        setSuccess('Attendance records updated successfully!');
        cancelAttendanceEdit();
        await loadData();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update attendance records');
    } finally {
      setSavingAttendance(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/lecturer/students')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!student) return <div>No student found</div>;

  const presentCount = attendance.filter(a => a.status === 'Present').length;
  const absentCount = attendance.filter(a => a.status === 'Absent').length;
  const presentPercentage = attendance.length > 0 ? ((presentCount / attendance.length) * 100).toFixed(1) : 0;
  const filteredAttendance = attendance.filter(record => {
    const matchesDate = !attendanceDateFilter || record.session_date?.split('T')[0] === attendanceDateFilter;
    const status = (editingAttendance ? attendanceDraft[String(record.session_id)] : record.status) || 'Absent';
    const matchesStatus = attendanceStatusFilter === 'all' || status.toLowerCase() === attendanceStatusFilter;
    return matchesDate && matchesStatus;
  });
  const visibleAttendance = editingAttendance ? attendance : filteredAttendance;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => navigate('/lecturer/students')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
        <ArrowLeft className="w-4 h-4" /> Back to Students
      </button>
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">{success}</div>
      )}

      {/* Student Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
              {student.avatar_thumb_path || student.avatar_path ? (
                <button onClick={() => setShowPreview(true)} className="w-full h-full p-0 m-0 block cursor-pointer" title="View photo">
                  <img src={(student.avatar_thumb_path || student.avatar_path) ? getAvatarUrl(student.avatar_thumb_path || student.avatar_path) : ''} alt="avatar" className="w-full h-full object-cover" />
                </button>
              ) : (
                <span className="text-2xl font-bold text-indigo-600">{student.full_name?.charAt(0)}</span>
              )}

              {/* Preview modal */}
              {showPreview && (student.avatar_path || student.avatar_thumb_path) && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={() => setShowPreview(false)}>
                  <div className="bg-white rounded-lg overflow-hidden shadow-lg max-w-3xl max-h-[90vh]">
                    <button className="absolute right-4 top-4 text-white bg-black bg-opacity-40 rounded-full p-2" onClick={(e) => { e.stopPropagation(); setShowPreview(false); }}>Close</button>
                    <img src={getAvatarUrl(student.avatar_path || student.avatar_thumb_path)} alt="avatar-large" className="max-w-full max-h-[80vh] object-contain" />
                  </div>
                </div>
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{student.full_name}</h1>
              <p className="text-gray-500 mt-1">{student.student_id_number || 'N/A'}</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">{student.grade_name}</span>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 text-gray-600">
            <Mail className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{student.email}</p>
            </div>
          </div>
          {student.phone_number && (
            <div className="flex items-center gap-3 text-gray-600">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{student.phone_number}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 text-gray-600">
            <Clock className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Last Login</p>
              <p className="font-medium">{formatDateTime(student.last_login)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sessions</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{attendance.length}</p>
            </div>
            <Calendar className="w-12 h-12 text-gray-200" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-green-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Present</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{presentCount}</p>
              <p className="text-xs text-green-500 mt-1">{presentPercentage}%</p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-200" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-red-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Absent</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{absentCount}</p>
            </div>
            <XCircle className="w-12 h-12 text-red-200" />
          </div>
        </div>
      </div>

      {/* Attendance Records */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Attendance Records</h2>
            <p className="text-gray-500 text-sm mt-1">
              {editingAttendance ? attendance.length : `${filteredAttendance.length} of ${attendance.length}`} sessions shown
            </p>
          </div>
          {!editingAttendance ? (
            <button onClick={startAttendanceEdit} className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition">
              <Edit className="w-4 h-4" /> Edit Attendance
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={cancelAttendanceEdit} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => setShowAttendanceConfirmation(true)} disabled={savingAttendance} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                <Save className="w-4 h-4" /> Save Changes
              </button>
            </div>
          )}
        </div>
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setShowAttendanceDateFilter(prev => !prev)}
              className={`p-2 rounded-lg border transition ${
                attendanceDateFilter
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
              title="Filter attendance by date"
              aria-label="Filter attendance by date"
              aria-expanded={showAttendanceDateFilter}
            >
              <Calendar className="w-5 h-5" />
            </button>
            {showAttendanceDateFilter && (
              <input
                type="date"
                value={attendanceDateFilter}
                onChange={(event) => setAttendanceDateFilter(event.target.value)}
                className="w-auto max-w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                aria-label="Attendance date"
                autoFocus
              />
            )}
          </div>
          <select
            value={attendanceStatusFilter}
            onChange={(event) => setAttendanceStatusFilter(event.target.value)}
            className="w-auto max-w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
            aria-label="Filter attendance by status"
          >
            <option value="all">All statuses</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
          </select>
          {(attendanceDateFilter || attendanceStatusFilter !== 'all') && (
            <button
              type="button"
              onClick={() => { setAttendanceDateFilter(''); setAttendanceStatusFilter('all'); setShowAttendanceDateFilter(false); }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>
        <div className={editingAttendance ? 'overflow-x-auto' : 'max-h-[28rem] overflow-auto'}>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleAttendance.map((record) => (
                <tr
                  key={record.session_id}
                  onClick={editingAttendance ? () => toggleAttendanceStatus(record) : undefined}
                  className={`hover:bg-gray-50 ${editingAttendance ? 'cursor-pointer' : ''}`}
                  title={editingAttendance ? 'Click anywhere on this row to switch attendance status' : undefined}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-gray-800">{record.subject_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(record.session_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                        (editingAttendance ? attendanceDraft[String(record.session_id)] : record.status) === 'Present'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                      {(editingAttendance ? attendanceDraft[String(record.session_id)] : record.status) || 'Absent'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">
                    {record.session_description || '-'}
                  </td>
                </tr>
              ))}
              {visibleAttendance.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-400">
                    No attendance records yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ConfirmationModal
          isOpen={showAttendanceConfirmation}
          onClose={() => setShowAttendanceConfirmation(false)}
          onConfirm={saveAttendanceChanges}
          title="Save attendance changes?"
          message={`Update attendance for ${Object.values(attendanceDraft).filter(Boolean).length} selected date(s) for ${student.full_name}?`}
          confirmText="Save Attendance"
          cancelText="Cancel"
          type="info"
        />
      </div>

      {/* Marks Records */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Exam Marks</h2>
          <p className="text-gray-500 text-sm mt-1">{marks.length} exams recorded</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Exam</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Marks</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Percentage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {marks.map((mark) => (
                <tr key={mark.mark_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-gray-800">{mark.subject_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-800">{mark.exam_name}</td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(mark.exam_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-gray-800">
                      {mark.obtained_marks} / {mark.total_marks}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      mark.percentage >= 70 
                        ? 'bg-green-100 text-green-700'
                        : mark.percentage >= 50
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {parseFloat(mark.percentage).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
              {marks.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                    No marks recorded yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
